import { useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { CellStyle, ColDef } from 'ag-grid-community';
import type { CellValueChangedEvent } from 'ag-grid-community/dist/types/core/events';
import TableGridItem from '../UI/TableGridItem';
import { DraftUpdater, ScenarioDraftEntity, newEntityDraftRow } from './scenarioDraft';
import {
    generateMonthColumns,
    MONTH_KEY,
    SCENARIO_GRID_CLASS,
    SCENARIO_HEADER_HEIGHT,
    SCENARIO_ROW_HEIGHT,
    SCENARIO_MONTH_COL_WIDTH,
    scenarioEntityColumn,
    scenarioFillerColumn,
} from './helpers';

/** A pickable row entity — a partner location, for the one type still using this grid. */
export interface ScenarioValueEntity {
    entityId: number;
    entityLabel: string;
}

export interface ScenarioValueGridProps {
    months: string[];
    /** The scope multiselect's selection — this IS the row set (CALL-783). */
    entityIds: number[];
    /** The editable draft: the monthly series of every entity that already has one. Read for RENDERING only —
     *  an edit is derived from the `previous` an updater is handed, never from this prop (see DraftUpdater). */
    draft: ScenarioDraftEntity[];
    onChange: (update: DraftUpdater<ScenarioDraftEntity[]>) => void;
    /** The bucket's entities (worktypes/locations) — the source of the row labels. */
    entities: ScenarioValueEntity[];
    isLoading: boolean;
    canEdit: boolean;
    /** Identifier column header. */
    entityHeader: string;
    /** Cell rendering/parsing — Minute-price shows percent and keeps the typed decimals. */
    formatValue: (value: unknown) => string;
    parseValue: (rawValue: unknown) => number | null;
    labels: {
        noRows: string;
    };
}

// The input grid of the Minute-price scenario type: rows are the partner/locations the scope multiselect
// picked, columns are the scenario's months, cells are the monthly percentage changes (may be negative).
// Inputs only — the calculated impact is the section's other view.
//
// Kept generic over an "entity" because it was shared with Contacts until that type gained its per-worktype
// training block (CALL-783 Stage 4) and moved to ScenarioRowGrid, whose row-of-scalars-plus-months shape it
// now needs. Minute-price is the only caller left; the entity indirection is what a second such type would
// re-use, and collapsing it would only have to be undone.
//
// Rows follow the SELECTION, not the saved values: an entity is on screen because the user picked it above,
// and typing the first number into it is what gives it a draft entry. Adding and removing rows therefore
// happens in exactly one place (ScenarioScopeField), which is why this grid has neither an ADD button nor a
// trash column any more.
const ScenarioValueGrid = ({
    months,
    entityIds,
    draft,
    onChange,
    entities,
    isLoading,
    canEdit,
    entityHeader,
    formatValue,
    parseValue,
    labels,
}: ScenarioValueGridProps) => {
    const labelByEntity = useMemo(() => {
        const map = new Map<number, string>();
        for (const entity of entities) map.set(entity.entityId, entity.entityLabel);
        // A persisted label wins over none at all: an entity the bucket no longer offers still reads as a
        // name rather than a bare id.
        for (const row of draft) if (row.entityLabel) map.set(row.entityId, row.entityLabel);
        return map;
    }, [entities, draft]);

    const valuesByEntity = useMemo(() => new Map(draft.map((row) => [row.entityId, row.monthValues])), [draft]);

    // Sorted for display only; the draft keeps its own order, which carries no meaning (the payload is
    // reconciled server-side).
    const rowData = useMemo(
        () =>
            entityIds
                .map((entityId) => ({
                    entityId,
                    entityLabel: labelByEntity.get(entityId) ?? `#${entityId}`,
                    ...(valuesByEntity.get(entityId) ?? {}),
                }))
                .sort((a, b) => a.entityLabel.localeCompare(b.entityLabel)),
        [entityIds, labelByEntity, valuesByEntity]
    );

    // Reports an UPDATER, so a gesture that fills several cells at once keeps all of them: ag-grid flushes the
    // whole burst of cellValueChanged events in ONE macrotask, and rebuilding from the `draft` prop made every
    // handler start from the same pre-burst state (see DraftUpdater in scenarioDraft). Everything the new draft
    // is built from comes from `previous` — the "does this entity have a row yet?" test included, since an
    // earlier cell of the same paste may be what created it.
    const onCellValueChanged = useCallback(
        (event: CellValueChangedEvent) => {
            const { data, colDef, newValue } = event;
            if (!data?.entityId || !colDef.field || !MONTH_KEY.test(colDef.field)) return;
            const month = colDef.field;
            const entityId: number = data.entityId;
            // A cleared cell drops out entirely — 0 is a value the user may mean, '' is not.
            const cleared = newValue == null || newValue === '' || Number.isNaN(Number(newValue));

            onChange((previous) => {
                if (!previous.some((row) => row.entityId === entityId)) {
                    // The row exists because the entity is selected, not because it holds anything yet — so a
                    // cell the user only emptied leaves the draft as it is rather than creating one.
                    if (cleared) return previous;
                    // The persisted label can only come from a row, and there is none; the bucket's options are
                    // the only source left (and the one labelByEntity would fall back to anyway).
                    const label = entities.find((entity) => entity.entityId === entityId)?.entityLabel ?? null;
                    const created = newEntityDraftRow(entityId, label);
                    return [...previous, { ...created, monthValues: { [month]: Number(newValue) } }];
                }

                return previous.map((row) => {
                    if (row.entityId !== entityId) return row;
                    const monthValues = { ...row.monthValues };
                    if (cleared) delete monthValues[month];
                    else monthValues[month] = Number(newValue);
                    return { ...row, monthValues };
                });
            });
        },
        [onChange, entities]
    );

    const monthColumns = useMemo(
        () =>
            generateMonthColumns(months, {
                // A `width`, not the helper's minWidth: these columns are flex:0, where ag-grid sizes by
                // width and defaults it to 200px — see SCENARIO_MONTH_COL_WIDTH.
                width: SCENARIO_MONTH_COL_WIDTH,
                editable: canEdit,
                valueFormatter: (params) => formatValue(params.value),
                valueParser: (params) => {
                    if (params.newValue === '' || params.newValue == null) return null;
                    return parseValue(params.newValue);
                },
                cellStyle: (): CellStyle =>
                    canEdit ? { backgroundColor: '#fffde7', textAlign: 'right' } : { textAlign: 'right' },
            }),
        [months, canEdit, formatValue, parseValue]
    );

    const columnDefs: ColDef[] = useMemo(
        // The filler soaks up the width a short period leaves over — 770px of columns in a 1487px shell on
        // a five-month scenario, before CALL-784.
        () => [
            { ...scenarioEntityColumn(entityHeader), field: 'entityLabel', editable: false },
            ...monthColumns,
            scenarioFillerColumn(),
        ],
        [entityHeader, monthColumns]
    );

    // filter:false everywhere → the header menu (kebab) would be empty noise; hide it.
    const defaultColDef: ColDef = useMemo(
        () => ({ sortable: true, filter: false, resizable: true, suppressMenu: true }),
        []
    );

    return (
        <Box className={SCENARIO_GRID_CLASS}>
            <TableGridItem
                height="auto"
                domLayout="autoHeight"
                headerHeight={SCENARIO_HEADER_HEIGHT}
                rowHeight={SCENARIO_ROW_HEIGHT}
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                getRowId={(params) => String(params.data.entityId)}
                loading={isLoading}
                enableRangeSelection
                // CALL-794: commit a cell the user is still typing in when focus leaves the grid. Set per
                // grid rather than on the shared wrapper — see ScenarioRowGrid for the reasoning.
                stopEditingWhenCellsLoseFocus
                onCellValueChanged={onCellValueChanged}
                // Rows here come from the form's multiselect alone, so the empty state has no action to
                // carry — only the sentence that names the control that fills it (CALL-784).
                noRowsOverlayComponent={() => (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 1.5, textAlign: 'center' }}>
                        {labels.noRows}
                    </Typography>
                )}
            />
        </Box>
    );
};

export default ScenarioValueGrid;
